import { useCallback, useState } from 'react';
import { useCreateWorkOrderDocument } from '@api/enterprise/workOrderDocuments';
import { dataUrlToBase64 } from '@api/enterprise/workOrderDocuments';
import { EnterpriseSyncApi } from '@api/bff/openapi/endpoints/enterprise-sync-api';
import { bffApiClient } from '@api/bff/client';
import { WorkOrdersApi, WorkOrderDocumentType } from '@api/enterprise/openapi';
import { enterpriseApiClient } from '@api/enterprise/client';
import { useUserStore } from '@stores/userStore';
import useOnlineStatus from '@hooks/useOnlineStatus';
import type { EventDocumentViewModel } from '@api/bff/openapi/models';
import type { WorkOrderDocumentViewModel } from '@api/enterprise/openapi';
import type { EquipmentReviewData } from '../types';

type PhotoToSubmit = {
  dataUrl: string;
  caption: string | null;
  assetId: string;
  isCoverPhoto: boolean;
  photoIndex: number;
};

type UseSubmitPromotedPhotosParams = {
  jobNumber: string;
  equipmentList: EquipmentReviewData[];
};

const enterpriseSyncApi = new EnterpriseSyncApi(
  undefined,
  undefined,
  bffApiClient
);

const workOrdersApi = new WorkOrdersApi(
  undefined,
  undefined,
  enterpriseApiClient
);

const fetchWorkOrderDocuments = async (
  jobNumber: string
): Promise<WorkOrderDocumentViewModel[]> => {
  try {
    const response = await workOrdersApi.apiWorkOrdersJobNumberDocumentsGet({
      jobNumber
    });
    return response.data;
  } catch {
    return [];
  }
};

const documentExists = (
  existingDocuments: WorkOrderDocumentViewModel[],
  documentCompany: string,
  documentName: string
): boolean => {
  return existingDocuments.some(
    doc =>
      doc.documentCompany === documentCompany &&
      doc.documentName === documentName
  );
};

const fetchEventDocuments = async (
  eventNumber: number
): Promise<EventDocumentViewModel[]> => {
  try {
    const response =
      await enterpriseSyncApi.apiEnterpriseSyncEventDocumentsEventNumberGet({
        eventNumber
      });
    return response.data;
  } catch {
    return [];
  }
};

const matchPhotoByIndex = (
  photoIndex: number,
  eventDocuments: EventDocumentViewModel[]
): EventDocumentViewModel | null => {
  const expectedDocNumber = `PHOTO-${String(photoIndex + 1).padStart(3, '0')}`;
  return (
    eventDocuments.find(doc => doc.documentNumber === expectedDocNumber) ?? null
  );
};

const base64ToDataUrl = (base64: string, mimeType = 'image/png'): string => {
  if (base64.startsWith('data:')) {
    return base64;
  }
  return `data:${mimeType};base64,${base64}`;
};

export const useSubmitPromotedPhotos = ({
  jobNumber,
  equipmentList
}: UseSubmitPromotedPhotosParams) => {
  const userName = useUserStore(state => state.userName) ?? '';
  const isOnline = useOnlineStatus();
  const { mutateAsync: createDocument } = useCreateWorkOrderDocument();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getPhotosToSubmit = useCallback((): {
    assetId: string;
    eventNum: number | null;
    coverPhotoIndex: number | null;
    promotedPhotoIndices: number[];
    fallbackCoverUrl: string | null;
    promotedPhotos: { thumbnailDataUrl: string; caption: string | null }[];
  }[] => {
    return equipmentList
      .filter(
        item =>
          item.promotedPhotoIndices.length > 0 || item.coverPhotoIndex !== null
      )
      .map(item => ({
        assetId: item.assetId,
        eventNum: item.eventNum,
        coverPhotoIndex: item.coverPhotoIndex,
        promotedPhotoIndices: item.promotedPhotoIndices,
        fallbackCoverUrl: item.coverImageUrl,
        promotedPhotos: item.promotedPhotos.map(p => ({
          thumbnailDataUrl: p.thumbnailDataUrl,
          caption: p.caption
        }))
      }));
  }, [equipmentList]);

  const submitPhotos = useCallback(async (): Promise<{
    successful: number;
    failed: number;
    skipped: number;
  }> => {
    if (!isOnline) {
      return { successful: 0, failed: 0, skipped: 0 };
    }

    const itemsWithPhotos = getPhotosToSubmit();

    if (itemsWithPhotos.length === 0) {
      return { successful: 0, failed: 0, skipped: 0 };
    }

    setIsSubmitting(true);
    let successful = 0;
    let failed = 0;
    let skipped = 0;

    try {
      const existingDocuments = await fetchWorkOrderDocuments(jobNumber);

      for (const itemData of itemsWithPhotos) {
        let eventDocuments: EventDocumentViewModel[] = [];

        if (itemData.eventNum) {
          eventDocuments = await fetchEventDocuments(itemData.eventNum);
        }

        const photosForItem: PhotoToSubmit[] = [];

        if (itemData.coverPhotoIndex !== null) {
          const isCoverAlsoPromoted = itemData.promotedPhotoIndices.includes(
            itemData.coverPhotoIndex
          );

          if (!isCoverAlsoPromoted) {
            let coverDataUrl = itemData.fallbackCoverUrl;

            const coverDoc = matchPhotoByIndex(
              itemData.coverPhotoIndex,
              eventDocuments
            );
            if (coverDoc?.fileContent) {
              coverDataUrl = base64ToDataUrl(coverDoc.fileContent);
            }

            if (coverDataUrl) {
              photosForItem.push({
                dataUrl: coverDataUrl,
                caption: 'Cover Photo',
                assetId: itemData.assetId,
                isCoverPhoto: true,
                photoIndex: itemData.coverPhotoIndex
              });
            }
          }
        }

        itemData.promotedPhotoIndices.forEach((photoIndex, i) => {
          const promotedPhoto = itemData.promotedPhotos[i];
          let dataUrl = promotedPhoto?.thumbnailDataUrl ?? '';

          const doc = matchPhotoByIndex(photoIndex, eventDocuments);
          if (doc?.fileContent) {
            dataUrl = base64ToDataUrl(doc.fileContent);
          }

          if (dataUrl) {
            photosForItem.push({
              dataUrl,
              caption: promotedPhoto?.caption ?? null,
              assetId: itemData.assetId,
              isCoverPhoto: false,
              photoIndex
            });
          }
        });

        for (let i = 0; i < photosForItem.length; i++) {
          const photo = photosForItem[i];
          const documentName = photo.isCoverPhoto
            ? `COVER_${photo.assetId}`
            : `PHOTO_${photo.assetId}_${i + 1}`;
          const documentCompany = '*ATTACH*';

          if (
            documentExists(existingDocuments, documentCompany, documentName)
          ) {
            skipped++;
            continue;
          }

          try {
            const base64Data = dataUrlToBase64(photo.dataUrl);

            await createDocument({
              jobNumber,
              documentData: {
                jobNumber,
                documentCompany,
                documentType: WorkOrderDocumentType.Attachment,
                documentName,
                addedBy: userName.toUpperCase(),
                fileName: `${documentName}.PNG`,
                fileAttachment: base64Data,
                signatureText: photo.caption || undefined
              },
              showToast: false
            });

            successful++;
          } catch (error) {
            console.error(
              `Failed to submit photo for ${photo.assetId}:`,
              error
            );
            failed++;
          }
        }
      }
    } finally {
      setIsSubmitting(false);
    }

    return { successful, failed, skipped };
  }, [getPhotosToSubmit, createDocument, jobNumber, userName, isOnline]);

  const hasPhotosToSubmit = equipmentList.some(
    item =>
      item.promotedPhotoIndices.length > 0 || item.coverPhotoIndex !== null
  );

  const photoCount = equipmentList.reduce((count, item) => {
    let itemPhotoCount = item.promotedPhotoIndices.length;
    if (
      item.coverPhotoIndex !== null &&
      !item.promotedPhotoIndices.includes(item.coverPhotoIndex)
    ) {
      itemPhotoCount += 1;
    }
    return count + itemPhotoCount;
  }, 0);

  return {
    submitPhotos,
    isSubmitting,
    hasPhotosToSubmit,
    photoCount
  };
};
